/* eslint-disable jsx-a11y/anchor-is-valid */
// This is a workaround for the fact that Google does not crawl buttons.

import React, {
    FC, useState, useEffect, MouseEvent, TouchEvent, KeyboardEvent
} from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import { ArrowLeftShape, ArrowRightShape } from "@themuse/design-system";
import { pushUrl } from "utils/strings";
import styles from "./Pagination.module.scss";

export interface PaginationProps {
    activePage: number;
    setActivePage: React.Dispatch<number>;
    totalPages?: number;
    pagesToShow?: number;
    onPageChange?: (page: number) => void;
    paginationContainerOverride?: string;
    paginationControlsOverride?: string;
    pageNumbersContainerOverride?: string;
    pageButtonOverride?: string;
    arrowButtonOverride?: string;
}

export const Pagination: FC<PaginationProps> = ({
    activePage = 1,
    setActivePage,
    totalPages = 1,
    pagesToShow = 5,
    onPageChange = () => { },
    paginationContainerOverride = "",
    paginationControlsOverride = "",
    pageNumbersContainerOverride = "",
    pageButtonOverride = "",
    arrowButtonOverride = "",
}) => {
    const router = useRouter();
    const [pathname, setPathname] = useState("");

    useEffect(() => {
        // Update pathname when URL changes (asPath) but only the path portion.
        setPathname(router.asPath.split("?")[0].split("#")[0]);
    }, [router.asPath]);

    useEffect(() => {
        pushUrl(activePage, window.location.pathname);
    }, [activePage]);

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setActivePage(page);
            onPageChange(page);
            pushUrl(page, window.location.pathname);
        }
    };

    // Calculate which page numbers to display
    const getPageNumbers = () => {
        const pageNumbers = [];
        const numberLine = Math.min(pagesToShow, totalPages);

        let startPage = Math.max(1, activePage - Math.floor(numberLine / 2));
        const endPage = Math.min(totalPages, startPage + numberLine - 1);

        // Adjust startPage if we're near the end
        startPage = Math.max(1, endPage - numberLine + 1);

        for (let i = startPage; i <= endPage; i += 1) {
            pageNumbers.push(i);
        }

        return pageNumbers;
    };

    const handlePaginationInteraction = (
        event: MouseEvent<HTMLAnchorElement> | TouchEvent<HTMLAnchorElement> | KeyboardEvent<HTMLAnchorElement>
    ) => {
        const page = parseInt((event.target as unknown as HTMLElement)?.dataset?.page ?? "0", 10);
        event.preventDefault();
        handlePageChange(page);
        onPageChange(page);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handlePaginationInteraction(e as KeyboardEvent<HTMLAnchorElement>);
        }
    };
    const previousPage = activePage - 1 >= 1 ? activePage - 1 : null;
    const nextPage = activePage + 1 <= totalPages ? activePage + 1 : null;
    const pageNumbers = getPageNumbers();
    const arrowStyles = `${styles.pageButton} ${pageButtonOverride} ${styles.arrowButton} ${arrowButtonOverride}`;
    if (totalPages <= 1) return null;

    return (
        <nav className={`${styles.paginationContainer} ${paginationContainerOverride}`} aria-label="Pagination">
            <div className={`${styles.paginationControls} ${paginationControlsOverride}`}>
                <Link
                    href={{
                        pathname,
                        // never query string to page 1
                        ...(previousPage > 1 ? { query: { page: previousPage } } : null)
                    }}
                    shallow={true}
                    passHref={true}
                >
                    <a
                        aria-label="Go to previous page"
                        className={`${arrowStyles} ${previousPage ? "" : styles.disabled}`}
                        data-page={activePage - 1}
                        onClick={handlePaginationInteraction}
                        onKeyDown={handleKeyPress}
                        rel={previousPage ? "" : "nofollow"}
                        role="link"
                        tabIndex={0}
                    >
                        <ArrowLeftShape />
                    </a>
                </Link>

                <div className={`${styles.pageNumbersContainer} ${pageNumbersContainerOverride}`}>
                    {pageNumbers.map((pageNumber) => (
                        <Link
                            href={{
                                pathname,
                                // never query string to page 1
                                ...(pageNumber > 1 ? { query: { page: pageNumber } } : {})
                            }}
                            shallow={true}
                            key={pageNumber}
                        >
                            <a
                                aria-current={pageNumber === activePage ? "page" : undefined}
                                aria-label={`Go to page ${pageNumber}`}
                                className={`${styles.pageButton} ${pageButtonOverride}`}
                                data-page={pageNumber}
                                onClick={handlePaginationInteraction}
                                onKeyDown={handleKeyPress}
                                role="link"
                                tabIndex={0}
                            >
                                {pageNumber}
                            </a>
                        </Link>
                    ))}
                </div>
                <Link
                    href={{
                        pathname,
                        ...(nextPage ? { query: { page: nextPage } } : {})
                    }}
                    shallow={true}
                >
                    <a
                        aria-label="Go to next page"
                        className={`${arrowStyles} ${nextPage ? "" : styles.disabled}`}
                        data-page={activePage + 1}
                        onClick={handlePaginationInteraction}
                        onKeyDown={handleKeyPress}
                        rel={nextPage ? "" : "nofollow"}
                        role="link"
                        tabIndex={0}
                    >
                        <ArrowRightShape />
                    </a>
                </Link>
            </div>
        </nav>
    );
};
Pagination.displayName = "Pagination";
